const DEFAULT_LOGO_FILENAME = "logo.png";

function normalizeDownloadFileName(value: string): string | undefined {
  const fileName = value.trim().split(/[\\/]/).pop()?.trim();
  const hasControlCharacter = [...(fileName ?? "")].some((character) => {
    const characterCode = character.charCodeAt(0);
    return characterCode <= 0x1f || characterCode === 0x7f;
  });

  return fileName && !hasControlCharacter ? fileName : undefined;
}

export function getFileNameFromContentDisposition(contentDispositionHeader: unknown, fallbackFileName: string): string {
  const contentDisposition = Array.isArray(contentDispositionHeader)
    ? contentDispositionHeader[0]
    : contentDispositionHeader;

  if (typeof contentDisposition !== "string") {
    return fallbackFileName;
  }

  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8'[^']*'(?:"([^"]*)"|([^;]+))/i);
  const encodedFileName = encodedMatch?.[1] ?? encodedMatch?.[2];

  if (encodedFileName) {
    try {
      return (
        normalizeDownloadFileName(decodeURIComponent(encodedFileName.trim().replace(/\+/g, "%20"))) ?? fallbackFileName
      );
    } catch {
      return fallbackFileName;
    }
  }

  const quotedMatch = contentDisposition.match(/filename\s*=\s*"((?:\\.|[^"])*)"/i);
  const unquotedMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  const fileName = quotedMatch?.[1]?.replace(/\\(["\\])/g, "$1") ?? unquotedMatch?.[1];

  return (fileName && normalizeDownloadFileName(fileName)) || fallbackFileName;
}

export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/**
 * Extracts the filename from an S3 path or any file path
 * @param path - The file path (e.g., "brand/uuid/logo.png")
 * @returns The filename or default filename if extraction fails
 * @example
 * getFileNameFromPath("brand/123/logo.png") // returns "logo.png"
 * getFileNameFromPath(undefined) // returns "logo.png"
 */
export function getFileNameFromPath(path?: string): string {
  return path?.split("/").pop() || DEFAULT_LOGO_FILENAME;
}

/**
 * Fetches a file from a URL and converts it to a File object
 * @param url - The URL to fetch the file from (can be a presigned S3 URL)
 * @param filePath - Optional path to extract the filename from
 * @returns A File object with the fetched content
 * @throws Error if the fetch fails
 * @example
 * const file = await fetchFileFromUrl("https://example.com/image.png")
 * const file = await fetchFileFromUrl("https://s3.../logo.png", "brand/123/logo.png")
 */
export async function fetchFileFromUrl(url: string, filePath?: string): Promise<File> {
  const response = await fetch(url, { credentials: "include" });

  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const blob = await response.blob();
  const fileName = getFileNameFromPath(filePath);

  return new File([blob], fileName, { type: blob.type });
}
